/*
 * CAPTURE-OWNED MODULE
 * OCR readiness helpers shared by the template create/edit flows.
 *
 * The template flows poll /api/ocr-words/stats waiting for OCR words to appear.
 * Some documents never produce words — they parked in a terminal non-OCR state
 * (e.g. flagged as a duplicate and never enqueued for OCR). For those, polling
 * to the timeout and reporting "OCR is still processing" is misleading. The
 * stats response now includes the document's `status`; map the terminal ones to
 * an accurate, actionable message so the caller can stop polling early.
 */

// Statuses where OCR will never produce words for this document, so continuing
// to wait is pointless. Keep in sync with capture_documents status values.
const TERMINAL_OCR_MESSAGES = {
  // RETIRED IN B4: "Delete the existing copy or choose a different file."
  // It told the user to destroy the ORIGINAL — the one document we know is
  // good — to unblock a copy, which is exactly backwards. It was only ever
  // defensible while there was nothing else to do; the Held tab is now the
  // something else, and it is named here so the message ends somewhere.
  duplicate_pending_review:
    'This file matches a document already captured. It is held on the Processing ' +
    'page under Held, where you can process it anyway or discard it.',
  duplicate_discarded:
    'This document was discarded as a duplicate, so it was not processed.',
  rejected: 'This document was rejected, so it was not processed.',
  failed: 'Processing failed for this document. Try uploading it again.',
  ocr_failed: 'OCR failed for this document. Try uploading it again.',
  // Slice 3c: a batch held for a review of where its documents begin. It is not
  // processed as one document, so its words are not coming through this path.
  pending_boundary_review:
    'This file is a batch of several documents waiting for a review of where each one begins. ' +
    'It is not processed as a single document.',
};

/**
 * Returns an accurate, actionable message when `status` is a terminal non-OCR
 * state (OCR will never produce words), or null when the document may still be
 * processing and polling should continue.
 *
 * @param {string|null|undefined} status capture_documents.status from the
 *   /api/ocr-words/stats response.
 * @returns {string|null}
 */
export function terminalOcrMessage(status) {
  if (!status) return null;
  return TERMINAL_OCR_MESSAGES[status] || null;
}
